import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Save, Loader2, CheckCircle, User, Mail, Building, Phone, MapPin, Lock, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const ProfileField = ({ icon: Icon, label, name, value, onChange, type = 'text', disabled = false, placeholder = '' }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5 text-gray-400" /> {label}
    </label>
    <input
      type={type}
      name={name}
      value={value || ''}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder}
      className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:ring-2 focus:ring-sky-300 focus:border-sky-400 outline-none disabled:bg-gray-50 disabled:text-gray-500"
      data-testid={`profile-${name}`}
    />
  </div>
);

const Profile = () => {
  const { user, token, updateUser } = useAuth();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', address: '', city: '', state: '', zip_code: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showPwForm, setShowPwForm] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        email: user.email || '',
        company: user.company || '',
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        zip_code: user.zip_code || '',
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setSaved(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast({ title: 'Error', description: 'Name is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/api/auth/profile`, {
        name: form.name.trim(),
        company: form.company.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        zip_code: form.zip_code.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (res.data?.user) {
        updateUser(res.data.user);
      }
      setSaved(true);
      toast({ title: 'Profile Updated', description: 'Your information has been saved.' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to update profile', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Compose preview address
  const fullAddress = [form.address, form.city, [form.state, form.zip_code].filter(Boolean).join(' ')].filter(Boolean).join(', ');

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPw !== pwForm.confirm) {
      toast({ title: 'Error', description: 'New passwords do not match', variant: 'destructive' });
      return;
    }
    if (pwForm.newPw.length < 6) {
      toast({ title: 'Error', description: 'Password must be at least 6 characters', variant: 'destructive' });
      return;
    }
    setPwSaving(true);
    try {
      await axios.post(`${API}/api/auth/change-password`, {
        current_password: pwForm.current,
        new_password: pwForm.newPw,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Password Changed', description: 'Your password has been updated.' });
      setShowPwForm(false);
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to change password', variant: 'destructive' });
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto" data-testid="profile-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="profile-title">My Profile</h1>
          <p className="text-sm text-gray-500 mt-1">Update your personal and delivery information</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="bg-slate-50 px-6 py-4 border-b">
            <h2 className="font-semibold text-slate-700 flex items-center gap-2">
              <User className="w-4 h-4 text-sky-600" /> Personal Information
            </h2>
          </div>

          <div className="p-6 space-y-4">
            <ProfileField icon={User} label="Full Name" name="name" value={form.name} onChange={handleChange} />
            <ProfileField icon={Mail} label="Email" name="email" value={form.email} onChange={handleChange} disabled />
            <ProfileField icon={Building} label="Company" name="company" value={form.company} onChange={handleChange} />
            <ProfileField icon={Phone} label="Phone" name="phone" value={form.phone} onChange={handleChange} type="tel" />
          </div>

          <div className="bg-slate-50 px-6 py-4 border-t border-b">
            <h2 className="font-semibold text-slate-700 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-teal-600" /> Delivery Address
            </h2>
          </div>

          <div className="p-6 space-y-4">
            <ProfileField icon={MapPin} label="Street Address" name="address" value={form.address} onChange={handleChange}
              placeholder="e.g., 11615 Brittmoore Park Drive" />
            <div className="grid grid-cols-3 gap-3">
              <ProfileField icon={MapPin} label="City" name="city" value={form.city} onChange={handleChange}
                placeholder="Houston" />
              <ProfileField icon={MapPin} label="State" name="state" value={form.state} onChange={handleChange}
                placeholder="TX" />
              <ProfileField icon={MapPin} label="ZIP Code" name="zip_code" value={form.zip_code} onChange={handleChange}
                placeholder="77041" />
            </div>
            {fullAddress && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-sm text-teal-800" data-testid="address-preview">
                <span className="font-medium">Ship To: </span>{fullAddress}
              </div>
            )}
          </div>

          <div className="px-6 py-4 bg-slate-50 border-t flex items-center justify-between">
            {saved && (
              <span className="text-sm text-green-600 flex items-center gap-1" data-testid="profile-saved-msg">
                <CheckCircle className="w-4 h-4" /> Saved
              </span>
            )}
            {!saved && <div />}
            <Button type="submit" disabled={saving} className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-semibold px-6" data-testid="profile-save-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
            </Button>
          </div>
        </form>

        {/* Change Password Section */}
        <div className="mt-6 bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="change-password-section">
          <div className="bg-slate-50 px-6 py-4 border-b flex items-center justify-between">
            <h2 className="font-semibold text-slate-700 flex items-center gap-2">
              <Lock className="w-4 h-4 text-amber-600" /> Password
            </h2>
            <Button variant="outline" size="sm" onClick={() => setShowPwForm(!showPwForm)} data-testid="change-password-toggle">
              {showPwForm ? 'Cancel' : 'Change Password'}
            </Button>
          </div>
          {showPwForm && (
            <form onSubmit={handleChangePassword} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5 text-gray-400" /> Current Password
                </label>
                <div className="relative">
                  <input type={showPw ? 'text' : 'password'} value={pwForm.current} onChange={e => setPwForm({...pwForm, current: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm pr-10 focus:ring-2 focus:ring-sky-300 focus:border-sky-400 outline-none"
                    required data-testid="current-password-input" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5 text-gray-400" /> New Password
                </label>
                <input type={showPw ? 'text' : 'password'} value={pwForm.newPw} onChange={e => setPwForm({...pwForm, newPw: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-300 focus:border-sky-400 outline-none"
                  required minLength={6} data-testid="new-password-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5 text-gray-400" /> Confirm New Password
                </label>
                <input type={showPw ? 'text' : 'password'} value={pwForm.confirm} onChange={e => setPwForm({...pwForm, confirm: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-300 focus:border-sky-400 outline-none"
                  required minLength={6} data-testid="confirm-password-input" />
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={pwSaving} className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-semibold px-6" data-testid="change-password-submit">
                  {pwSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Changing...</> : <><Lock className="w-4 h-4 mr-2" /> Update Password</>}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Profile;
